import React from 'react'
import { Box, Typography, makeStyles, Theme } from '@material-ui/core'

import {
    EMPTY_SOURCE_COUNTS,
    LeadSourceCountKey,
    LeadSourceCounts,
} from '../../hooks/usePipelineCounts'

export const LEAD_SOURCE_BARS: {
    key: LeadSourceCountKey
    label: string
    filterValues: string[]
    color: string
}[] = [
    {
        key: 'website',
        label: 'Website',
        filterValues: ['website', 'leadpipe'],
        color: '#6570E6',
    },
    {
        key: 'email',
        label: 'Campaign',
        filterValues: ['email'],
        color: '#7c3aed',
    },
    {
        key: 'phone',
        label: 'Phone',
        filterValues: ['phone'],
        color: '#0d9488',
    },
    {
        key: 'property',
        label: 'Walk-in',
        filterValues: ['property'],
        color: '#d97706',
    },
]

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        flex: '1 1 360px',
        minWidth: 280,
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 1.25),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
    },
    rows: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 2,
        width: '100%',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '86px 1fr 28px',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0, 0.25),
        border: 'none',
        borderRadius: 4,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        minHeight: 12,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    rowActive: {
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(25, 118, 210, 0.06)',
        '&:hover': {
            backgroundColor:
                theme.palette.type === 'dark'
                    ? 'rgba(25, 118, 210, 0.16)'
                    : 'rgba(25, 118, 210, 0.1)',
        },
    },
    label: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1.15,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    labelActive: {
        color: theme.palette.text.primary,
    },
    track: {
        height: 4,
        borderRadius: 999,
        backgroundColor: theme.palette.action.hover,
        overflow: 'hidden',
    },
    fill: {
        height: '100%',
        borderRadius: 999,
        minWidth: 0,
        transition: 'width 160ms ease',
    },
    count: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1.15,
        fontFamily: 'monospace',
        textAlign: 'right',
        color: theme.palette.text.primary,
    },
}))

function sourceGroupActive(
    selectedSources: string[],
    filterValues: string[],
): boolean {
    return filterValues.every((value) => selectedSources.includes(value))
}

interface LeadSourcesChartProps {
    counts?: LeadSourceCounts | null
    selectedSources: string[]
    loading?: boolean
    onSourcesChange: (sources: string[]) => void
}

export const LeadSourcesChart: React.FC<LeadSourcesChartProps> = ({
    counts,
    selectedSources,
    loading = false,
    onSourcesChange,
}) => {
    const classes = useStyles()
    const resolved = counts ?? EMPTY_SOURCE_COUNTS
    const values = LEAD_SOURCE_BARS.map((bar) => resolved[bar.key] ?? 0)
    const maxValue = Math.max(...values, 1)

    const toggleSource = (filterValues: string[]) => {
        if (sourceGroupActive(selectedSources, filterValues)) {
            onSourcesChange(
                selectedSources.filter(
                    (value) => !filterValues.includes(value),
                ),
            )
            return
        }
        const next = new Set(selectedSources)
        filterValues.forEach((value) => next.add(value))
        onSourcesChange(Array.from(next))
    }

    return (
        <Box className={classes.root} aria-label="Lead sources">
            <Box className={classes.rows}>
                {LEAD_SOURCE_BARS.map((bar, index) => {
                    const value = values[index]
                    const widthPct =
                        value <= 0 ? 0 : Math.max(4, (value / maxValue) * 100)
                    const isActive = sourceGroupActive(
                        selectedSources,
                        bar.filterValues,
                    )

                    return (
                        <Box
                            key={bar.key}
                            className={`${classes.row} ${
                                isActive ? classes.rowActive : ''
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={() => toggleSource(bar.filterValues)}
                            onKeyDown={(event) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    event.preventDefault()
                                    toggleSource(bar.filterValues)
                                }
                            }}
                        >
                            <Typography
                                className={`${classes.label} ${
                                    isActive ? classes.labelActive : ''
                                }`}
                            >
                                {bar.label}
                            </Typography>
                            <Box className={classes.track}>
                                <Box
                                    className={classes.fill}
                                    style={{
                                        width: loading ? 0 : `${widthPct}%`,
                                        backgroundColor: bar.color,
                                    }}
                                />
                            </Box>
                            <Typography className={classes.count}>
                                {loading ? '—' : value}
                            </Typography>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}
